/**
 * Single source of truth for business-hours awareness and the response-time
 * label shown on every quote CTA, form success state, and toast.
 *
 * Business hours: Mon–Sat, 8:00 AM – 6:00 PM (visitor's local time).
 * Sunday is closed. This matches the copy on /contact ("we're available
 * Monday through Saturday").
 */

export const BUSINESS_HOURS = {
  /** 0 = Sun, 1 = Mon, ... 6 = Sat */
  openDays: [1, 2, 3, 4, 5, 6] as const,
  openHour: 8, // 08:00
  closeHour: 18, // 18:00
} as const;

export function isWithinBusinessHours(date: Date = new Date()): boolean {
  const day = date.getDay();
  const hour = date.getHours();
  return (
    (BUSINESS_HOURS.openDays as readonly number[]).includes(day) &&
    hour >= BUSINESS_HOURS.openHour &&
    hour < BUSINESS_HOURS.closeHour
  );
}

/**
 * Short, reusable label for response time. Used everywhere — CTA subtext,
 * tooltips, toast descriptions, success cards.
 *
 * - During hours: "within 1 business day" (the historical promise)
 * - Off hours: "Calls returned during business hours" — sets expectations
 *   that we won't ring at 11pm.
 */
export function getResponseTimeLabel(date: Date = new Date()): string {
  return isWithinBusinessHours(date)
    ? "within 1 business day"
    : "Calls returned during business hours";
}

/**
 * Channel-aware variant for success toasts / cards where copy reads
 * "We'll {text|call} you ___".
 */
export function getChannelResponseLine(
  channel: "call" | "text",
  date: Date = new Date(),
): string {
  if (isWithinBusinessHours(date)) {
    return channel === "text"
      ? "We'll text you within 1 business day."
      : "A fence expert will call within 1 business day.";
  }
  // Off-hours: lead the message with the business-hours framing.
  return channel === "text"
    ? "Calls returned during business hours — we'll text you first thing."
    : "Calls returned during business hours — you're next on the list.";
}

/** Human-readable summary, e.g. for tooltips: "Mon–Sat, 8am–6pm". */
export const BUSINESS_HOURS_LABEL = "Mon–Sat, 8am–6pm";
